/**
 * AddressBookCard
 *
 * Manages org-scoped saved delivery addresses for use in Split Delivery and
 * standard single-delivery flows. Customers can:
 *   • View all saved addresses (default starred at top)
 *   • Add new entries
 *   • Edit existing entries
 *   • Mark one as default (auto-clears any previous default)
 *   • Delete entries
 *
 * Backed by `/api/organizations/{org_id}/address-book`.
 */
import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import CountrySelect from './CountrySelect';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
    BookMarked, MapPin, Plus, Pencil, Trash2, Star, Phone, Mail, Loader2, Check, X
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const EMPTY_FORM = {
    label: '',
    company_name: '',
    attention_to: '',
    street: '',
    postal_code: '',
    city: '',
    country: '',
    phone: '',
    delivery_email: '',
    note: '',
    is_default: false,
};

function authHeaders() {
    return { Authorization: `Bearer ${localStorage.getItem('token')}` };
}

export default function AddressBookCard({ orgId }) {
    const [entries, setEntries] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editingId, setEditingId] = useState(null); // null | 'new' | <entry.id>
    const [form, setForm] = useState(EMPTY_FORM);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!orgId) return;
        let cancelled = false;
        (async () => {
            try {
                const res = await fetch(`${API_URL}/api/organizations/${orgId}/address-book`, { headers: authHeaders() });
                if (!res.ok) throw new Error('Failed to load address book');
                const data = await res.json();
                if (!cancelled) setEntries(data);
            } catch (e) {
                if (!cancelled) toast.error(e.message || 'Could not load address book');
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [orgId]);

    const openNew = () => {
        setForm(EMPTY_FORM);
        setEditingId('new');
    };

    const openEdit = (entry) => {
        setForm({
            label: entry.label || '',
            company_name: entry.company_name || '',
            attention_to: entry.attention_to || '',
            street: entry.street || '',
            postal_code: entry.postal_code || '',
            city: entry.city || '',
            country: entry.country || '',
            phone: entry.phone || '',
            delivery_email: entry.delivery_email || '',
            note: entry.note || '',
            is_default: !!entry.is_default,
        });
        setEditingId(entry.id);
    };

    const cancelEdit = () => {
        setEditingId(null);
        setForm(EMPTY_FORM);
    };

    const validate = () => {
        const required = ['label', 'street', 'postal_code', 'city', 'country', 'phone'];
        for (const f of required) {
            if (!form[f] || !form[f].trim()) {
                toast.error(`Field "${f.replace('_', ' ')}" is required`);
                return false;
            }
        }
        return true;
    };

    const save = async () => {
        if (!validate()) return;
        setSaving(true);
        try {
            const body = {
                label: form.label,
                company_name: form.company_name || null,
                attention_to: form.attention_to || null,
                street: form.street,
                postal_code: form.postal_code,
                city: form.city,
                country: form.country,
                phone: form.phone,
                delivery_email: form.delivery_email || null,
                note: form.note || null,
                is_default: !!form.is_default,
            };
            const isNew = editingId === 'new';
            const url = isNew
                ? `${API_URL}/api/organizations/${orgId}/address-book`
                : `${API_URL}/api/organizations/${orgId}/address-book/${editingId}`;
            const res = await fetch(url, {
                method: isNew ? 'POST' : 'PATCH',
                headers: { ...authHeaders(), 'Content-Type': 'application/json' },
                body: JSON.stringify(body),
            });
            if (!res.ok) {
                const data = await res.json().catch(() => ({}));
                throw new Error(data.detail || 'Save failed');
            }
            const saved = await res.json();
            // Refresh full list so default-flag is consistent
            const refresh = await fetch(`${API_URL}/api/organizations/${orgId}/address-book`, { headers: authHeaders() });
            setEntries(refresh.ok ? await refresh.json() : (isNew ? [...entries, saved] : entries.map(e => e.id === saved.id ? saved : e)));
            toast.success(isNew ? 'Address added' : 'Address updated');
            cancelEdit();
        } catch (e) {
            toast.error(e.message || 'Save failed');
        } finally {
            setSaving(false);
        }
    };

    const remove = async (entry) => {
        if (!window.confirm(`Delete "${entry.label}"? This cannot be undone.`)) return;
        try {
            const res = await fetch(`${API_URL}/api/organizations/${orgId}/address-book/${entry.id}`, {
                method: 'DELETE',
                headers: authHeaders(),
            });
            if (!res.ok) throw new Error('Delete failed');
            setEntries(entries.filter(e => e.id !== entry.id));
            toast.success('Address removed');
        } catch (e) {
            toast.error(e.message || 'Delete failed');
        }
    };

    return (
        <Card className="mb-6" data-testid="address-book-card">
            <CardHeader>
                <div className="flex items-center justify-between">
                    <div>
                        <CardTitle className="text-lg flex items-center gap-2">
                            <BookMarked className="h-5 w-5 text-indigo-500" />
                            Saved Delivery Addresses
                        </CardTitle>
                        <CardDescription>
                            Reusable destinations for split delivery and standard orders.
                        </CardDescription>
                    </div>
                    {editingId === null && (
                        <Button
                            size="sm"
                            variant="outline"
                            onClick={openNew}
                            data-testid="address-book-add-btn"
                        >
                            <Plus className="h-3.5 w-3.5 mr-1" />
                            Add address
                        </Button>
                    )}
                </div>
            </CardHeader>
            <CardContent className="space-y-3">
                {loading ? (
                    <div className="flex items-center gap-2 text-sm text-slate-500 py-4">
                        <Loader2 className="h-4 w-4 animate-spin" /> Loading...
                    </div>
                ) : (
                    <>
                        {entries.length === 0 && editingId === null && (
                            <div className="text-sm text-slate-500 py-6 text-center border border-dashed rounded-lg">
                                No saved addresses yet. Add your first to speed up future orders.
                            </div>
                        )}

                        {entries.map((entry) => (
                            editingId === entry.id ? (
                                <AddressForm
                                    key={entry.id}
                                    form={form}
                                    setForm={setForm}
                                    saving={saving}
                                    onSave={save}
                                    onCancel={cancelEdit}
                                />
                            ) : (
                                <AddressRow
                                    key={entry.id}
                                    entry={entry}
                                    onEdit={() => openEdit(entry)}
                                    onDelete={() => remove(entry)}
                                />
                            )
                        ))}

                        {editingId === 'new' && (
                            <AddressForm
                                form={form}
                                setForm={setForm}
                                saving={saving}
                                onSave={save}
                                onCancel={cancelEdit}
                                isNew
                            />
                        )}
                    </>
                )}
            </CardContent>
        </Card>
    );
}

function AddressRow({ entry, onEdit, onDelete }) {
    return (
        <div
            className="flex items-start gap-3 p-3 border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors"
            data-testid={`address-row-${entry.id}`}
        >
            <MapPin className="h-4 w-4 text-slate-400 mt-1 flex-shrink-0" />
            <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                    <span className="font-medium text-slate-900">{entry.label}</span>
                    {entry.is_default && (
                        <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 font-semibold">
                            <Star className="h-2.5 w-2.5 fill-current" /> Default
                        </span>
                    )}
                </div>
                <div className="text-xs text-slate-600 space-y-0.5">
                    {entry.company_name && <p>{entry.company_name}</p>}
                    {entry.attention_to && <p className="text-slate-500">Attn: {entry.attention_to}</p>}
                    <p>{entry.street}</p>
                    <p>{entry.postal_code} {entry.city}, {entry.country}</p>
                    <div className="flex items-center gap-3 text-slate-500 pt-0.5">
                        {entry.phone && (
                            <span className="flex items-center gap-1"><Phone className="h-3 w-3" />{entry.phone}</span>
                        )}
                        {entry.delivery_email && (
                            <span className="flex items-center gap-1"><Mail className="h-3 w-3" />{entry.delivery_email}</span>
                        )}
                    </div>
                    {entry.note && <p className="italic text-slate-400 pt-0.5">&ldquo;{entry.note}&rdquo;</p>}
                </div>
            </div>
            <div className="flex items-center gap-1 flex-shrink-0">
                <Button
                    size="sm"
                    variant="ghost"
                    className="h-7 px-2"
                    onClick={onEdit}
                    data-testid={`address-edit-${entry.id}`}
                >
                    <Pencil className="h-3.5 w-3.5" />
                </Button>
                <Button
                    size="sm"
                    variant="ghost"
                    className="h-7 px-2 text-red-600"
                    onClick={onDelete}
                    data-testid={`address-delete-${entry.id}`}
                >
                    <Trash2 className="h-3.5 w-3.5" />
                </Button>
            </div>
        </div>
    );
}

function AddressForm({ form, setForm, saving, onSave, onCancel, isNew }) {
    const onChange = (field, value) => setForm({ ...form, [field]: value });
    return (
        <div className="p-4 border border-indigo-200 bg-indigo-50/30 rounded-lg space-y-3" data-testid={isNew ? 'address-form-new' : 'address-form-edit'}>
            <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                    <Label className="text-xs">Label *</Label>
                    <Input
                        value={form.label}
                        onChange={(e) => onChange('label', e.target.value)}
                        placeholder="e.g. Main Warehouse"
                        className="h-9"
                        data-testid="ab-label"
                    />
                </div>
                <div className="space-y-1">
                    <Label className="text-xs">Company / Attn</Label>
                    <Input
                        value={form.company_name}
                        onChange={(e) => onChange('company_name', e.target.value)}
                        className="h-9"
                        data-testid="ab-company"
                    />
                </div>
            </div>
            <div className="space-y-1">
                <Label className="text-xs">Attention To</Label>
                <Input
                    value={form.attention_to}
                    onChange={(e) => onChange('attention_to', e.target.value)}
                    className="h-9"
                    data-testid="ab-attention"
                />
            </div>
            <div className="space-y-1">
                <Label className="text-xs">Street Address *</Label>
                <Input
                    value={form.street}
                    onChange={(e) => onChange('street', e.target.value)}
                    className="h-9"
                    data-testid="ab-street"
                />
            </div>
            <div className="grid grid-cols-3 gap-3">
                <div className="space-y-1">
                    <Label className="text-xs">Postal Code *</Label>
                    <Input
                        value={form.postal_code}
                        onChange={(e) => onChange('postal_code', e.target.value)}
                        className="h-9"
                        data-testid="ab-postal"
                    />
                </div>
                <div className="space-y-1">
                    <Label className="text-xs">City *</Label>
                    <Input
                        value={form.city}
                        onChange={(e) => onChange('city', e.target.value)}
                        className="h-9"
                        data-testid="ab-city"
                    />
                </div>
                <div className="space-y-1">
                    <Label className="text-xs">Country *</Label>
                    <CountrySelect
                        value={form.country}
                        onChange={(code) => onChange('country', code)}
                        testid="ab-country"
                        className="h-9"
                    />
                </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                    <Label className="text-xs">Phone *</Label>
                    <Input
                        value={form.phone}
                        onChange={(e) => onChange('phone', e.target.value)}
                        className="h-9"
                        data-testid="ab-phone"
                    />
                </div>
                <div className="space-y-1">
                    <Label className="text-xs">Delivery Email</Label>
                    <Input
                        type="email"
                        value={form.delivery_email}
                        onChange={(e) => onChange('delivery_email', e.target.value)}
                        className="h-9"
                        data-testid="ab-email"
                    />
                </div>
            </div>
            <div className="space-y-1">
                <Label className="text-xs">Note / Instructions</Label>
                <Input
                    value={form.note}
                    onChange={(e) => onChange('note', e.target.value)}
                    placeholder="Optional"
                    className="h-9"
                    data-testid="ab-note"
                />
            </div>
            <label className="flex items-center gap-2 text-sm cursor-pointer pt-1">
                <input
                    type="checkbox"
                    checked={form.is_default}
                    onChange={(e) => onChange('is_default', e.target.checked)}
                    className="rounded"
                    data-testid="ab-default"
                />
                <span className="text-slate-700">Set as default address</span>
                <span className="text-xs text-slate-500">(pre-selected on new orders)</span>
            </label>
            <div className="flex items-center justify-end gap-2 pt-2 border-t border-indigo-200">
                <Button variant="outline" size="sm" onClick={onCancel} data-testid="ab-cancel">
                    <X className="h-3.5 w-3.5 mr-1" /> Cancel
                </Button>
                <Button size="sm" onClick={onSave} disabled={saving} data-testid="ab-save">
                    {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Check className="h-3.5 w-3.5 mr-1" />}
                    {isNew ? 'Add address' : 'Save changes'}
                </Button>
            </div>
        </div>
    );
}
